import { QueryClient, UseQueryOptions, useMutation, useQuery, useQueryClient } from 'react-query'
import { useAppSelector } from 'app/state'
import { ProviderType } from 'features/connections'
import { useEffect, useState } from 'react'
import {
  completeSFPartnerConnectTasks,
  getOnboardingSFPartnerConnect
} from 'features/onboarding-checklist/api/onboardingChecklist'
import { OnboardingChecklistProps } from 'features/onboarding-checklist/types'

const SF_CONNECT_KEY_ROOT = 'sfConnectTasks'

export const sfConnectTasksQueryKeys = {
  all: [SF_CONNECT_KEY_ROOT] as const,
  tasks: [SF_CONNECT_KEY_ROOT, 'sfConnectTasks'],
  task: (id: string) => [SF_CONNECT_KEY_ROOT, 'sfConnectTask', id]
}

const handleTasksCallback = (data: OnboardingChecklistProps, queryClient?: QueryClient) => {
  const { completed } = data || {}
  if (queryClient && completed) {
    queryClient.invalidateQueries(sfConnectTasksQueryKeys.tasks)
  }
}

const sfConnecTasksQuery: UseQueryOptions<OnboardingChecklistProps, Error> = {
  queryKey: sfConnectTasksQueryKeys.tasks,
  queryFn: () => getOnboardingSFPartnerConnect(),
  staleTime: Infinity,
  onSuccess: handleTasksCallback
}

export function useSFConnectCompleteTasksMutation(onError) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: completeSFPartnerConnectTasks,
    onSuccess: (data) => handleTasksCallback(data, queryClient),
    onError
  })
}

export function useSFConnectTasksQuery() {
  const sfPartnerConnect = useAppSelector((state) => state.account.data?.partnerConnect === ProviderType.SNOWFLAKE)
  const { data: response, isLoading } = useQuery(sfConnecTasksQuery)
  const { tasks, completed } = response || {}
  const isSFUserOnboarding = sfPartnerConnect && !completed && !isLoading
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (isSFUserOnboarding) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
  }, [setIsOpen, isSFUserOnboarding])

  return {
    isSFUserOnboarding,
    isSFPartnerConnectUser: sfPartnerConnect,
    tasks,
    isOpen,
    isLoading
  }
}
